import { useState, useRef } from 'react';
import { Timer, Zap, RotateCw } from 'lucide-react';

// ─── Constants ────────────────────────────────────────────────────────────────

const GUT_PROMPTS = [
  {
    icon: '⚡',
    text: 'Whatever your gut said in the first 2 seconds — do that.',
    sub:  'You already answered this before you opened the app.',
  },
  {
    icon: '👥',
    text: 'Ask the next person you see. Their answer is your answer.',
    sub:  'Stop using your own brain. It\'s compromised by fear.',
  },
  {
    icon: '🧠',
    text: 'You already know. Stop reading this.',
    sub:  'The analysis is just procrastination in a costume.',
  },
  {
    icon: '🔄',
    text: 'What would you tell a close friend in your exact situation?',
    sub:  'You\'d tell them to stop overthinking. So stop.',
  },
  {
    icon: '🌬️',
    text: 'Imagine it\'s done. Which option makes you feel lighter?',
    sub:  'Your body already voted. Trust the answer.',
  },
  {
    icon: '⏰',
    text: 'You have 5 minutes to decide or the option disappears forever.',
    sub:  'Urgency cuts through everything. What do you pick?',
  },
  {
    icon: '🚫',
    text: 'The option you keep making excuses not to choose — that\'s it.',
    sub:  'Resistance points at exactly what you need to do.',
  },
  {
    icon: '🎯',
    text: 'Your future self already made this decision. Execute.',
    sub:  'In 5 years you\'ll wish you did it 5 years earlier.',
  },
];

// ─── Die Face SVG ─────────────────────────────────────────────────────────────

const DOT_MAP = {
  1: [[50, 50]],
  2: [[28, 28], [72, 72]],
  3: [[28, 28], [50, 50], [72, 72]],
  4: [[28, 28], [72, 28], [28, 72], [72, 72]],
  5: [[28, 28], [72, 28], [50, 50], [28, 72], [72, 72]],
  6: [[28, 22], [72, 22], [28, 50], [72, 50], [28, 78], [72, 78]],
};

const DieFace = ({ face, size = 120, glowing = false, dimmed = false }) => {
  const dots  = DOT_MAP[Math.min(6, Math.max(1, face))] || DOT_MAP[1];
  const r     = size * 0.095;
  const color = dimmed ? '#374151' : (glowing ? '#eab308' : '#84cc16');
  const stroke = dimmed ? '#1f2937' : (glowing ? '#ca8a04' : '#4ade80');
  const bg    = dimmed ? '#111827' : '#000000';

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      xmlns="http://www.w3.org/2000/svg"
      style={glowing ? { filter: 'drop-shadow(0 0 16px rgba(234,179,8,0.8))' } : {}}
    >
      {/* Die body */}
      <rect x="4" y="4" width="92" height="92" rx="16" ry="16"
        fill={bg} stroke={stroke} strokeWidth="3.5" />
      {/* Inner border */}
      <rect x="9" y="9" width="82" height="82" rx="12" ry="12"
        fill="none" stroke={color} strokeWidth="1" opacity="0.3" />
      {/* Dots */}
      {dots.map(([cx, cy], i) => (
        <circle key={i} cx={cx} cy={cy} r={r} fill={color} />
      ))}
    </svg>
  );
};

// ─── GUT CHECK Mode ───────────────────────────────────────────────────────────

const GutCheckMode = () => {
  const [text,    setText]    = useState('');
  const [phase,   setPhase]   = useState('input');   // 'input' | 'processing' | 'result'
  const [prompt,  setPrompt]  = useState(null);
  const [error,   setError]   = useState('');

  const handleCheck = () => {
    if (!text.trim()) {
      setError('TYPE YOUR DECISION FIRST.');
      return;
    }
    setError('');
    setPhase('processing');
    const picked = GUT_PROMPTS[Math.floor(Math.random() * GUT_PROMPTS.length)];
    setTimeout(() => {
      setPrompt(picked);
      setPhase('result');
    }, 1400);
  };

  const reset = () => {
    setPhase('input');
    setPrompt(null);
    setText('');
    setError('');
  };

  return (
    <div>
      {/* Orange mode banner */}
      <div className="bg-orange-500 text-black p-3 mb-4 sm:mb-6 skew-x-[-3deg]">
        <p className="font-black text-base sm:text-lg uppercase tracking-wide skew-x-[3deg]">
          YOUR INSTINCTS KNOW • STOP OVERTHINKING
        </p>
      </div>

      {phase === 'input' && (
        <>
          <textarea
            value={text}
            onChange={e => { setText(e.target.value); setError(''); }}
            placeholder="WHAT ARE YOU DECIDING?..."
            className="w-full bg-black border-3 sm:border-4 border-orange-500 p-4 sm:p-5 text-lime-500 placeholder-gray-600 mb-4 sm:mb-6 min-h-32 font-bold text-lg sm:text-xl focus:outline-none focus:border-lime-500 uppercase resize-none"
          />
          {error && (
            <p className="text-red-400 font-black text-sm uppercase mb-3">{error}</p>
          )}
          <button
            onClick={handleCheck}
            className="w-full bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-400 hover:to-red-500 text-black py-5 sm:py-6 font-black text-2xl sm:text-3xl transition-all uppercase tracking-wider border-3 sm:border-4 border-orange-400 active:scale-95 touch-manipulation"
          >
            ⚡ GUT CHECK IT ⚡
          </button>
        </>
      )}

      {phase === 'processing' && (
        <div className="text-center py-10 sm:py-14">
          {/* Flickering "READING YOUR GUT" text */}
          <p
            className="text-5xl sm:text-7xl font-black text-orange-500 uppercase tracking-tight animate-gut-process mb-4"
            style={{ fontVariantNumeric: 'tabular-nums' }}
          >
            ⚡
          </p>
          <p className="text-orange-400 font-black text-xl uppercase tracking-widest animate-pulse">
            BYPASSING YOUR BRAIN...
          </p>
          <div className="mt-6 flex justify-center gap-2">
            {[0, 1, 2, 3, 4].map(i => (
              <div
                key={i}
                className="w-2 h-2 bg-orange-500 rounded-full animate-pulse"
                style={{ animationDelay: `${i * 0.12}s` }}
              />
            ))}
          </div>
        </div>
      )}

      {phase === 'result' && prompt && (
        <div className="animate-gut-reveal">
          {/* The decision context */}
          <div className="bg-gray-900 border-2 border-gray-700 p-3 mb-4">
            <p className="text-gray-500 text-xs font-black uppercase mb-1">YOUR DECISION</p>
            <p className="text-gray-300 font-bold text-sm leading-snug">{text}</p>
          </div>

          {/* The gut check answer */}
          <div
            className="bg-gradient-to-br from-orange-900 to-black border-3 sm:border-4 border-orange-500 p-6 sm:p-8 text-center mb-4"
            style={{ boxShadow: '0 0 40px rgba(249,115,22,0.4)' }}
          >
            <div className="text-5xl sm:text-6xl mb-4">{prompt.icon}</div>
            <p className="text-white text-xl sm:text-2xl md:text-3xl font-black leading-tight mb-3 uppercase tracking-tight">
              {prompt.text}
            </p>
            <p className="text-orange-400 text-sm sm:text-base font-bold uppercase tracking-wide">
              {prompt.sub}
            </p>
          </div>

          <div className="text-center mb-4">
            <p className="text-orange-500 font-black text-xl uppercase tracking-widest animate-pulse">
              STOP READING • GO EXECUTE
            </p>
          </div>

          <button
            onClick={reset}
            className="w-full bg-black text-orange-500 border-3 sm:border-4 border-orange-500 py-4 font-black uppercase text-lg tracking-wider hover:bg-orange-900 transition-all active:scale-95 touch-manipulation"
          >
            ANOTHER ONE
          </button>
        </div>
      )}
    </div>
  );
};

// ─── SEND IT DICE Mode ────────────────────────────────────────────────────────

const MAX_OPTS = 6;

const DiceMode = () => {
  const [options,    setOptions]    = useState(Array(MAX_OPTS).fill(''));
  const [phase,      setPhase]      = useState('input');   // 'input' | 'rolling' | 'result'
  const [dieFace,    setDieFace]    = useState(1);
  const [settling,   setSettling]   = useState(false);
  const [result,     setResult]     = useState(null);   // { index, text }
  const [error,      setError]      = useState('');
  const rollTimer = useRef(null);

  const filled = options.map(o => o.trim()).filter(Boolean);

  const updateOption = (i, val) => {
    const next = [...options];
    next[i] = val;
    setOptions(next);
    setError('');
  };

  const rollDice = () => {
    if (filled.length < 2) {
      setError('ENTER AT LEAST 2 OPTIONS TO ROLL');
      return;
    }
    setError('');

    // Pick winner before animation starts
    const winnerIdx  = Math.floor(Math.random() * filled.length);
    const winnerFace = winnerIdx + 1;  // 1-indexed die face

    setPhase('rolling');
    setSettling(false);

    // Rolling schedule: fast → medium → slow → settle
    const schedule = [
      ...Array(10).fill(80),   // fast
      ...Array(8).fill(130),   // medium
      ...Array(6).fill(200),   // slow
      ...Array(4).fill(330),   // very slow
    ];

    let step    = 0;
    let current = 1;

    const tick = () => {
      if (step >= schedule.length) {
        // Settle on winner
        setDieFace(winnerFace);
        setSettling(true);
        rollTimer.current = setTimeout(() => {
          setResult({ index: winnerIdx, text: filled[winnerIdx] });
          setPhase('result');
          setSettling(false);
        }, 500);
        return;
      }
      // Cycle through available faces (never stay on winning face during roll)
      const available = Array.from({ length: filled.length }, (_, i) => i + 1)
        .filter(f => f !== winnerFace || step < schedule.length - 2);
      current = available[Math.floor(Math.random() * available.length)] || current;
      setDieFace(current);
      step++;
      rollTimer.current = setTimeout(tick, schedule[step - 1]);
    };

    tick();
  };

  const reset = () => {
    if (rollTimer.current) clearTimeout(rollTimer.current);
    setPhase('input');
    setResult(null);
    setDieFace(1);
    setSettling(false);
    setOptions(Array(MAX_OPTS).fill(''));
    setError('');
  };

  return (
    <div>
      {/* Lime mode banner */}
      <div className="bg-lime-500 text-black p-3 mb-4 sm:mb-6 skew-x-[-3deg]">
        <p className="font-black text-base sm:text-lg uppercase tracking-wide skew-x-[3deg]">
          ROLL. COMMIT. EXECUTE. NO TAKEBACKS.
        </p>
      </div>

      {phase === 'input' && (
        <>
          <div className="space-y-2 mb-4 sm:mb-6">
            {options.map((opt, i) => (
              <div key={i} className="flex items-center gap-3">
                {/* Die face preview for this slot */}
                <div className="flex-shrink-0 opacity-60">
                  <DieFace face={i + 1} size={36} dimmed={!opt.trim()} />
                </div>
                <input
                  type="text"
                  value={opt}
                  onChange={e => updateOption(i, e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && i < MAX_OPTS - 1 && document.querySelectorAll('.dice-opt-input')[i + 1]?.focus()}
                  placeholder={i < 2 ? `OPTION ${i + 1} (required)` : `OPTION ${i + 1} (optional)`}
                  maxLength={60}
                  className={`dice-opt-input flex-1 bg-black border-2 p-3 font-bold text-sm focus:outline-none transition-all uppercase ${
                    opt.trim()
                      ? 'border-lime-500 text-lime-400 focus:border-lime-400'
                      : 'border-gray-800 text-gray-500 placeholder-gray-700 focus:border-gray-600'
                  }`}
                />
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between mb-3">
            <span className="text-gray-600 text-xs font-black uppercase">{filled.length} OPTIONS LOADED</span>
            {filled.length >= 2 && (
              <span className="text-lime-600 text-xs font-black uppercase">READY TO ROLL</span>
            )}
          </div>

          {error && (
            <p className="text-red-400 font-black text-sm uppercase mb-3">{error}</p>
          )}

          <button
            onClick={rollDice}
            disabled={filled.length < 2}
            className="w-full bg-gradient-to-r from-lime-500 to-green-600 hover:from-lime-400 hover:to-green-500 text-black py-5 sm:py-6 font-black text-2xl sm:text-3xl transition-all uppercase tracking-wider border-3 sm:border-4 border-lime-400 active:scale-95 disabled:opacity-30 disabled:cursor-not-allowed touch-manipulation flex items-center justify-center gap-3"
          >
            ⚄ ROLL THE DICE ⚄
          </button>
        </>
      )}

      {phase === 'rolling' && (
        <div className="text-center py-6 sm:py-8">
          {/* Rolling die */}
          <div className="flex justify-center mb-6">
            <div className={settling ? 'animate-dice-settle' : 'animate-dice-roll'}>
              <DieFace face={dieFace} size={140} />
            </div>
          </div>

          {/* Rolling options list */}
          <div className="space-y-1 mb-6">
            {filled.map((opt, i) => (
              <div
                key={i}
                className={`flex items-center gap-3 px-4 py-2 transition-all ${
                  dieFace === i + 1
                    ? 'bg-lime-900 border border-lime-500'
                    : 'bg-transparent border border-transparent opacity-30'
                }`}
              >
                <DieFace face={i + 1} size={24} glowing={dieFace === i + 1} />
                <span className={`font-black text-sm uppercase tracking-wide ${
                  dieFace === i + 1 ? 'text-lime-400' : 'text-gray-600'
                }`}>
                  {opt}
                </span>
              </div>
            ))}
          </div>

          <p className="text-lime-500 font-black text-xl uppercase tracking-widest animate-pulse">
            {settling ? 'SETTLING...' : 'ROLLING...'}
          </p>
        </div>
      )}

      {phase === 'result' && result && (
        <div className="animate-gut-reveal">
          {/* Winner die */}
          <div className="flex justify-center mb-6">
            <div style={{ animation: 'diceSettle 0.5s cubic-bezier(0.34,1.56,0.64,1) forwards' }}>
              <DieFace face={result.index + 1} size={140} glowing />
            </div>
          </div>

          {/* Result card */}
          <div
            className="bg-gradient-to-br from-lime-900 to-black border-3 sm:border-4 border-lime-500 p-6 sm:p-8 text-center mb-4"
            style={{ boxShadow: '0 0 50px rgba(132,204,22,0.5)' }}
          >
            <p className="text-lime-400 font-black text-sm uppercase tracking-widest mb-2">THE DICE HAVE SPOKEN</p>
            <p className="text-white text-2xl sm:text-3xl md:text-4xl font-black leading-tight mb-2 uppercase">
              {result.text}
            </p>
            <p className="text-lime-600 font-black text-sm uppercase tracking-widest">
              FACE {result.index + 1} — STOP READING. GO.
            </p>
          </div>

          {/* Option recap */}
          <div className="space-y-1 mb-4">
            {filled.map((opt, i) => (
              <div
                key={i}
                className={`flex items-center gap-3 px-3 py-2 border ${
                  i === result.index
                    ? 'border-lime-500 bg-lime-950'
                    : 'border-gray-800 opacity-40'
                }`}
              >
                <DieFace face={i + 1} size={22} glowing={i === result.index} />
                <span className={`font-bold text-sm uppercase ${i === result.index ? 'text-lime-400' : 'text-gray-600'}`}>
                  {opt}
                </span>
                {i === result.index && (
                  <span className="ml-auto text-lime-500 font-black text-xs uppercase">WINNER</span>
                )}
              </div>
            ))}
          </div>

          <p className="text-center text-lime-500 font-black text-xl uppercase tracking-widest animate-pulse mb-4">
            STOP READING • GO EXECUTE
          </p>

          <button
            onClick={reset}
            className="w-full bg-black text-lime-500 border-3 sm:border-4 border-lime-500 py-4 font-black uppercase text-lg tracking-wider hover:bg-lime-900 transition-all active:scale-95 touch-manipulation"
          >
            ROLL AGAIN
          </button>
        </div>
      )}
    </div>
  );
};

// ─── Main ExecuteMode ─────────────────────────────────────────────────────────

export const ExecuteMode = ({
  coinFlipMode, setCoinFlipMode,
  showResult, setShowResult,
  coinResult, setCoinResult,
  decisionText, setDecisionText,
  optionA, setOptionA,
  optionB, setOptionB,
  countdown,
  startDecisionCountdown,
  savedDecisions,
}) => {
  // activeMode drives which panel shows; syncs coinFlipMode prop for decision/coinflip
  const [activeMode, setActiveMode] = useState(coinFlipMode ? 'coinflip' : 'decision');

  const switchMode = (mode) => {
    setActiveMode(mode);
    if (mode === 'decision') {
      setCoinFlipMode(false);
      setShowResult(false);
      setCoinResult(null);
    } else if (mode === 'coinflip') {
      setCoinFlipMode(true);
      setShowResult(false);
      setCoinResult(null);
    }
  };

  const TABS = [
    { id: 'decision', label: 'DECIDE',    Icon: Timer,   activeClass: 'bg-yellow-500 text-black border-yellow-400',    inactiveClass: 'bg-black text-yellow-500 border-yellow-700 hover:bg-yellow-900' },
    { id: 'gutcheck', label: 'GUT CHECK', Icon: Zap,     activeClass: 'bg-orange-500 text-black border-orange-400',    inactiveClass: 'bg-black text-orange-500 border-orange-700 hover:bg-orange-900' },
    { id: 'coinflip', label: 'COIN FLIP', Icon: RotateCw, activeClass: 'bg-yellow-500 text-black border-yellow-400',   inactiveClass: 'bg-black text-yellow-500 border-yellow-700 hover:bg-yellow-900' },
    { id: 'dice',     label: 'DICE',      Icon: null,    activeClass: 'bg-lime-500 text-black border-lime-400',         inactiveClass: 'bg-black text-lime-500 border-lime-700 hover:bg-lime-900' },
  ];

  // Border color of the main card follows the active mode
  const cardBorderClass =
    activeMode === 'gutcheck' ? 'border-orange-500' :
    activeMode === 'dice'     ? 'border-lime-500'   :
    'border-yellow-500';

  return (
    <div className="space-y-4 animate-fade-in px-3 sm:px-4">
      <div className={`bg-gradient-to-br from-gray-900 to-black p-4 sm:p-6 border-3 sm:border-4 ${cardBorderClass} transition-colors duration-300`}>

        {/* Header */}
        <h2 className={`text-3xl sm:text-4xl font-black mb-4 sm:mb-5 uppercase tracking-tight flex items-center gap-2 sm:gap-3 border-b-3 sm:border-b-4 pb-3 sm:pb-4 transition-colors duration-300 ${
          activeMode === 'gutcheck' ? 'text-orange-500 border-orange-500' :
          activeMode === 'dice'     ? 'text-lime-500 border-lime-500' :
          'text-yellow-500 border-yellow-500'
        }`}>
          <Timer size={32} />
          EXECUTE MODE
        </h2>

        {/* 2×2 tab grid */}
        <div className="grid grid-cols-2 gap-2 mb-5">
          {TABS.map(({ id, label, Icon }) => (
            <button
              key={id}
              onClick={() => switchMode(id)}
              className={`flex items-center justify-center gap-2 py-3 font-black text-sm uppercase border-2 transition-all active:scale-95 touch-manipulation min-h-[52px] ${
                activeMode === id ? TABS.find(t => t.id === id).activeClass : TABS.find(t => t.id === id).inactiveClass
              }`}
            >
              {Icon && <Icon size={15} className="flex-shrink-0" />}
              {id === 'dice' && <span className="text-base leading-none">⚄</span>}
              <span className="leading-tight">{label}</span>
            </button>
          ))}
        </div>

        {/* ── DECISION MODE ──────────────────────────────────────── */}
        {activeMode === 'decision' && (
          <div>
            <div className="bg-yellow-500 text-black p-3 mb-4 sm:mb-6 skew-x-[-3deg]">
              <p className="font-black text-base sm:text-lg uppercase tracking-wide skew-x-[3deg]">
                10 SECONDS • NO HESITATION
              </p>
            </div>

            <textarea
              value={decisionText}
              onChange={e => setDecisionText(e.target.value)}
              placeholder="TYPE YOUR DECISION..."
              className="w-full bg-black border-3 sm:border-4 border-yellow-500 p-4 sm:p-5 text-lime-500 placeholder-gray-600 mb-4 sm:mb-6 min-h-32 font-bold text-lg sm:text-xl focus:outline-none focus:border-lime-500 uppercase resize-none"
              disabled={countdown !== null}
            />

            {countdown === null && !showResult && (
              <button
                onClick={startDecisionCountdown}
                className="w-full bg-gradient-to-r from-yellow-500 to-orange-600 hover:from-yellow-400 hover:to-orange-500 text-black py-5 sm:py-6 font-black text-2xl sm:text-3xl transition-all uppercase tracking-wider border-3 sm:border-4 border-yellow-400 active:scale-95 touch-manipulation"
              >
                🔥 START NOW 🔥
              </button>
            )}

            {countdown !== null && (
              <div className="text-center">
                <div className="text-8xl sm:text-9xl font-black mb-4 sm:mb-6 text-lime-500 animate-pulse">
                  {countdown}
                </div>
                <p className="text-3xl sm:text-4xl font-black text-yellow-500 uppercase tracking-wider animate-pulse">
                  DO IT NOW!
                </p>
              </div>
            )}

            {showResult && (
              <div className="text-center space-y-6 sm:space-y-8 py-8 sm:py-10 bg-gradient-to-br from-lime-500 to-green-600 border-3 sm:border-4 border-lime-400">
                <div className="text-4xl sm:text-6xl font-black text-black px-4">{showResult}</div>
                <p className="text-2xl sm:text-3xl font-black text-black uppercase tracking-wide px-4">
                  STOP READING • GO EXECUTE
                </p>
                <button
                  onClick={() => { setShowResult(false); setCoinResult(null); setDecisionText(''); }}
                  className="bg-black text-lime-500 border-3 sm:border-4 border-lime-500 px-8 sm:px-10 py-3 sm:py-4 font-black uppercase text-lg sm:text-xl tracking-wider hover:bg-lime-500 hover:text-black transition-all active:scale-95 touch-manipulation"
                >
                  ANOTHER ONE
                </button>
              </div>
            )}
          </div>
        )}

        {/* ── GUT CHECK MODE ─────────────────────────────────────── */}
        {activeMode === 'gutcheck' && <GutCheckMode />}

        {/* ── COIN FLIP MODE ─────────────────────────────────────── */}
        {activeMode === 'coinflip' && (
          <div>
            <div className="bg-yellow-500 text-black p-3 mb-4 sm:mb-6 skew-x-[-3deg]">
              <p className="font-black text-base sm:text-lg uppercase tracking-wide skew-x-[3deg]">
                3 SECONDS • LET FATE DECIDE
              </p>
            </div>

            <div className="space-y-3 mb-4 sm:mb-6">
              <div>
                <label className="block text-yellow-500 font-black text-sm mb-2 uppercase">OPTION A (HEADS)</label>
                <input
                  type="text" value={optionA}
                  onChange={e => setOptionA(e.target.value)}
                  placeholder="First option..." disabled={countdown !== null}
                  className="w-full bg-black border-3 border-lime-500 p-4 text-lime-500 placeholder-gray-600 font-bold text-lg focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-yellow-500 font-black text-sm mb-2 uppercase">OPTION B (TAILS)</label>
                <input
                  type="text" value={optionB}
                  onChange={e => setOptionB(e.target.value)}
                  placeholder="Second option..." disabled={countdown !== null}
                  className="w-full bg-black border-3 border-red-500 p-4 text-red-500 placeholder-gray-600 font-bold text-lg focus:outline-none"
                />
              </div>
            </div>

            {countdown === null && !showResult && (
              <button
                onClick={startDecisionCountdown}
                className="w-full bg-gradient-to-r from-yellow-500 to-orange-600 hover:from-yellow-400 hover:to-orange-500 text-black py-5 sm:py-6 font-black text-2xl sm:text-3xl transition-all uppercase tracking-wider border-3 sm:border-4 border-yellow-400 active:scale-95 touch-manipulation"
              >
                🔥 FLIP IT 🔥
              </button>
            )}

            {countdown !== null && (
              <div className="text-center">
                <div style={{ perspective: '400px' }} className="flex justify-center mb-4">
                  <div className="animate-coin-flip">
                    <div className="relative">
                      <svg
                        className="animate-coin-flip block"
                        width="120" height="120" viewBox="0 0 120 120"
                        xmlns="http://www.w3.org/2000/svg"
                        style={{ transformOrigin: 'center', backfaceVisibility: 'visible' }}
                      >
                        <defs>
                          <radialGradient id="coinGradFlip" cx="35%" cy="35%">
                            <stop offset="0%" stopColor="#fde68a"/>
                            <stop offset="50%" stopColor="#f59e0b"/>
                            <stop offset="100%" stopColor="#b45309"/>
                          </radialGradient>
                        </defs>
                        <circle cx="60" cy="60" r="55" fill="url(#coinGradFlip)" stroke="#92400e" strokeWidth="4"/>
                        <circle cx="60" cy="60" r="44" fill="none" stroke="#fbbf24" strokeWidth="2" opacity="0.6"/>
                        <text x="60" y="76" textAnchor="middle" fontSize="50" fontWeight="bold"
                              fontFamily="Arial Black, Arial" fill="#92400e">$</text>
                      </svg>
                      <div className="absolute -inset-4 bg-yellow-400/30 rounded-full blur-xl animate-pulse"/>
                    </div>
                  </div>
                </div>
                <div className="text-8xl sm:text-9xl font-black mb-4 sm:mb-6 text-lime-500 animate-pulse">
                  {countdown}
                </div>
                <p className="text-3xl sm:text-4xl font-black text-yellow-500 uppercase tracking-wider animate-pulse">
                  FLIPPING...
                </p>
              </div>
            )}

            {showResult && (
              <div className="text-center space-y-6 sm:space-y-8 py-8 sm:py-10 bg-gradient-to-br from-lime-500 to-green-600 border-3 sm:border-4 border-lime-400">
                {coinResult && (
                  <div style={{ perspective: '400px' }} className="flex justify-center mb-4">
                    <div className="relative">
                      <div className="animate-coin-bounce">
                        <svg
                          className="animate-coin-bounce block"
                          width="120" height="120" viewBox="0 0 120 120"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <defs>
                            <radialGradient id="coinGrad" cx="35%" cy="35%">
                              <stop offset="0%" stopColor="#fde68a"/>
                              <stop offset="50%" stopColor="#f59e0b"/>
                              <stop offset="100%" stopColor="#b45309"/>
                            </radialGradient>
                          </defs>
                          <circle cx="60" cy="60" r="55" fill="url(#coinGrad)" stroke="#92400e" strokeWidth="4"/>
                          <circle cx="60" cy="60" r="44" fill="none" stroke="#fbbf24" strokeWidth="2" opacity="0.6"/>
                          <text x="60" y="76" textAnchor="middle" fontSize="50" fontWeight="bold"
                                fontFamily="Arial Black, Arial" fill="#92400e">$</text>
                        </svg>
                      </div>
                      <div className="absolute -inset-4 bg-yellow-400/30 rounded-full blur-xl animate-pulse"/>
                    </div>
                  </div>
                )}
                <div className="text-4xl sm:text-6xl font-black text-black px-4">{showResult}</div>
                <p className="text-2xl sm:text-3xl font-black text-black uppercase tracking-wide px-4">
                  STOP READING • GO EXECUTE
                </p>
                <button
                  onClick={() => { setShowResult(false); setCoinResult(null); setOptionA(''); setOptionB(''); }}
                  className="bg-black text-lime-500 border-3 sm:border-4 border-lime-500 px-8 sm:px-10 py-3 sm:py-4 font-black uppercase text-lg sm:text-xl tracking-wider hover:bg-lime-500 hover:text-black transition-all active:scale-95 touch-manipulation"
                >
                  ANOTHER ONE
                </button>
              </div>
            )}
          </div>
        )}

        {/* ── DICE MODE ──────────────────────────────────────────── */}
        {activeMode === 'dice' && <DiceMode />}
      </div>

      {/* Saved decisions — only relevant for Decision mode */}
      {activeMode === 'decision' && savedDecisions.length > 0 && (
        <div className="bg-gray-900 border-3 sm:border-4 border-orange-500 p-4 sm:p-6">
          <h3 className="text-2xl font-black text-orange-500 uppercase mb-4 border-b-2 border-orange-500 pb-2">
            YOUR INDECISIONS ({savedDecisions.length})
          </h3>
          <p className="text-gray-400 text-sm font-bold mb-4">Things you're overthinking. Stop it.</p>
          <div className="space-y-3">
            {savedDecisions.slice(0, 5).map(decision => (
              <div key={decision.id} className="bg-black border-2 border-gray-700 p-3 sm:p-4">
                <p className="text-white font-bold text-sm sm:text-base mb-1">{decision.text}</p>
                <p className="text-gray-500 text-xs font-black uppercase">Saved: {decision.savedAt}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
